/**
 * Critical path method (CPM) and PERT three-point estimates for an activity-on-node network.
 *
 * Forward pass: ES = the largest EF among predecessors (0 with none), EF = ES + duration.
 * Backward pass: LF = the smallest LS among successors (the project duration with none),
 * LS = LF − duration. Total slack (float) = LS − ES; free slack = the smallest successor ES
 * minus EF (project duration minus EF at the end of the network). An activity is critical
 * when its total slack is zero. All dependencies are finish-to-start with no lag.
 *
 * PERT: expected time te = (a + 4m + b) ÷ 6, standard deviation σ = (b − a) ÷ 6. On a
 * critical path the expected times add and, assuming independent activities, so do the
 * variances; the path length is then treated as approximately normal.
 */

import { normalCdf, normalQuantile } from "@/lib/stats/distributions";

export const MAX_ACTIVITIES = 200;
const EPSILON = 1e-9;
const MAX_PATHS = 100;

export interface ActivityInput {
  id: string;
  name: string;
  duration: number;
  predecessors: string[];
  /** Optimistic, most likely and pessimistic times, when PERT is used. */
  estimate?: { a: number; m: number; b: number };
}

export interface Scheduled extends ActivityInput {
  successors: string[];
  es: number;
  ef: number;
  ls: number;
  lf: number;
  totalSlack: number;
  freeSlack: number;
  critical: boolean;
  /** σ² of the duration; 0 without a three-point estimate. */
  variance: number;
}

export type CpmError =
  | { kind: "empty" }
  | { kind: "tooMany" }
  | { kind: "duplicate"; id: string }
  | { kind: "selfLoop"; id: string }
  | { kind: "unknown"; id: string; predecessor: string }
  | { kind: "cycle"; ids: string[] };

export interface CpmResult {
  activities: Scheduled[];
  duration: number;
  criticalPaths: string[][];
  /** True when there were more critical paths than the cap. */
  pathsTruncated: boolean;
}

/** PERT expected time, standard deviation and variance. */
export function pertEstimate(
  a: number,
  m: number,
  b: number
): { te: number; sigma: number; variance: number } {
  const sigma = (b - a) / 6;
  return { te: (a + 4 * m + b) / 6, sigma, variance: sigma * sigma };
}

function validate(inputs: readonly ActivityInput[]): CpmError | null {
  if (inputs.length === 0) {
    return { kind: "empty" };
  }
  if (inputs.length > MAX_ACTIVITIES) {
    return { kind: "tooMany" };
  }
  const ids = new Set<string>();
  for (const activity of inputs) {
    if (ids.has(activity.id)) {
      return { kind: "duplicate", id: activity.id };
    }
    ids.add(activity.id);
  }
  for (const activity of inputs) {
    for (const predecessor of activity.predecessors) {
      if (predecessor === activity.id) {
        return { kind: "selfLoop", id: activity.id };
      }
      if (!ids.has(predecessor)) {
        return { kind: "unknown", id: activity.id, predecessor };
      }
    }
  }
  return null;
}

/** Activity ids in an order where every predecessor comes first; the leftovers if cyclic. */
function topologicalOrder(inputs: readonly ActivityInput[]): {
  order: string[];
  leftover: string[];
} {
  const remaining = new Map<string, number>();
  const successors = new Map<string, string[]>();
  for (const activity of inputs) {
    remaining.set(activity.id, new Set(activity.predecessors).size);
    successors.set(activity.id, []);
  }
  for (const activity of inputs) {
    for (const predecessor of new Set(activity.predecessors)) {
      successors.get(predecessor)?.push(activity.id);
    }
  }
  const queue = inputs
    .filter((activity) => remaining.get(activity.id) === 0)
    .map((activity) => activity.id);
  const order: string[] = [];
  for (const id of queue) {
    order.push(id);
    for (const next of successors.get(id) ?? []) {
      const left = (remaining.get(next) ?? 0) - 1;
      remaining.set(next, left);
      if (left === 0) {
        queue.push(next);
      }
    }
  }
  const done = new Set(order);
  return {
    order,
    leftover: inputs.map((a) => a.id).filter((id) => !done.has(id)),
  };
}

function criticalPaths(activities: readonly Scheduled[]): {
  paths: string[][];
  truncated: boolean;
} {
  const byId = new Map(activities.map((a) => [a.id, a]));
  const paths: string[][] = [];
  let truncated = false;
  const walk = (current: Scheduled, trail: string[]) => {
    if (paths.length >= MAX_PATHS) {
      truncated = true;
      return;
    }
    const next: Scheduled[] = [];
    for (const id of current.successors) {
      const s = byId.get(id);
      if (s?.critical && Math.abs(s.es - current.ef) < EPSILON) {
        next.push(s);
      }
    }
    if (next.length === 0) {
      paths.push(trail);
      return;
    }
    for (const s of next) {
      walk(s, [...trail, s.id]);
    }
  };
  for (const activity of activities) {
    if (activity.critical && activity.es < EPSILON) {
      walk(activity, [activity.id]);
    }
  }
  return { paths, truncated };
}

export function schedule(
  inputs: readonly ActivityInput[]
): CpmResult | { error: CpmError } {
  const problem = validate(inputs);
  if (problem) {
    return { error: problem };
  }
  const { order, leftover } = topologicalOrder(inputs);
  if (leftover.length > 0) {
    return { error: { kind: "cycle", ids: leftover } };
  }
  const input = new Map(inputs.map((a) => [a.id, a]));
  const successors = new Map<string, string[]>(inputs.map((a) => [a.id, []]));
  for (const activity of inputs) {
    for (const predecessor of new Set(activity.predecessors)) {
      successors.get(predecessor)?.push(activity.id);
    }
  }
  const es = new Map<string, number>();
  const ef = new Map<string, number>();
  for (const id of order) {
    const activity = input.get(id) as ActivityInput;
    let start = 0;
    for (const predecessor of activity.predecessors) {
      start = Math.max(start, ef.get(predecessor) ?? 0);
    }
    es.set(id, start);
    ef.set(id, start + activity.duration);
  }
  const duration = Math.max(...ef.values());
  const ls = new Map<string, number>();
  const lf = new Map<string, number>();
  for (const id of [...order].reverse()) {
    const activity = input.get(id) as ActivityInput;
    let finish = duration;
    for (const next of successors.get(id) ?? []) {
      finish = Math.min(finish, ls.get(next) ?? duration);
    }
    lf.set(id, finish);
    ls.set(id, finish - activity.duration);
  }
  const snap = (value: number) => (Math.abs(value) < EPSILON ? 0 : value);
  const activities: Scheduled[] = inputs.map((activity) => {
    const start = es.get(activity.id) as number;
    const finish = ef.get(activity.id) as number;
    const next = successors.get(activity.id) ?? [];
    let freeEnd = duration;
    for (const id of next) {
      freeEnd = Math.min(freeEnd, es.get(id) as number);
    }
    const total = (ls.get(activity.id) as number) - start;
    const sigma = activity.estimate
      ? (activity.estimate.b - activity.estimate.a) / 6
      : 0;
    return {
      ...activity,
      successors: next,
      es: start,
      ef: finish,
      ls: snap(ls.get(activity.id) as number),
      lf: snap(lf.get(activity.id) as number),
      totalSlack: snap(total),
      freeSlack: snap(Math.max(0, freeEnd - finish)),
      critical: Math.abs(total) < EPSILON,
      variance: sigma * sigma,
    };
  });
  const { paths, truncated } = criticalPaths(activities);
  return {
    activities,
    duration,
    criticalPaths: paths,
    pathsTruncated: truncated,
  };
}

export interface PertSummary {
  /** The critical path used: the one with the largest variance. */
  path: string[];
  mean: number;
  variance: number;
  sigma: number;
  /** Duration with 95% confidence of finishing: mean + 1.645σ. */
  confidence95: number;
  /** P(finish ≤ target), or null without a target. */
  probability: number | null;
  /** (target − mean) ÷ σ, or null. */
  z: number | null;
}

/** The PERT view of a scheduled network whose durations are expected times. */
export function pertSummary(
  result: CpmResult,
  target: number | null
): PertSummary | null {
  const byId = new Map(result.activities.map((a) => [a.id, a]));
  let best: PertSummary | null = null;
  for (const path of result.criticalPaths) {
    const variance = path.reduce(
      (sum, id) => sum + (byId.get(id)?.variance ?? 0),
      0
    );
    if (best === null || variance > best.variance + EPSILON) {
      best = {
        path,
        mean: result.duration,
        variance,
        sigma: Math.sqrt(variance),
        confidence95: 0,
        probability: null,
        z: null,
      };
    }
  }
  if (best === null) {
    return null;
  }
  best.confidence95 = best.mean + normalQuantile(0.95) * best.sigma;
  if (target !== null) {
    if (best.sigma > 0) {
      best.z = (target - best.mean) / best.sigma;
      best.probability = normalCdf(best.z);
    } else {
      best.probability = target >= best.mean ? 1 : 0;
    }
  }
  return best;
}

export type ParseMode = "cpm" | "pert";

export type ParseProblem =
  | "fields"
  | "duration"
  | "negative"
  | "order"
  | "emptyId";

export interface ParsedActivities {
  activities: ActivityInput[];
  problem: { line: number; text: string; reason: ParseProblem } | null;
}

const LINE_SPLIT = /\r?\n/;
const SEMICOLON_OR_TAB = /[\t;]/;
const PRED_SPLIT = /[\s,;/]+/;
const NO_PREDECESSOR = new Set(["-", "–", "—", "none", "n/a", "na"]);

function splitFields(line: string): string[] {
  const separator = SEMICOLON_OR_TAB.test(line) ? SEMICOLON_OR_TAB : ",";
  return line.split(separator).map((field) => field.trim());
}

function predecessorsOf(fields: readonly string[]): string[] {
  return fields
    .join(" ")
    .split(PRED_SPLIT)
    .filter(
      (token) => token !== "" && !NO_PREDECESSOR.has(token.toLowerCase())
    );
}

type LineResult = ActivityInput | ParseProblem | "header";

/** One split line to an activity, a problem, or "header" when the numbers are missing. */
function activityFromFields(
  fields: readonly string[],
  mode: ParseMode,
  parse: (text: string) => number | null
): LineResult {
  const needed = mode === "pert" ? 5 : 3;
  if (fields.length < needed) {
    return "fields";
  }
  const numbers = fields.slice(2, needed).map(parse);
  if (numbers.some((n) => n === null)) {
    return "header";
  }
  const values = numbers as number[];
  if (values.some((n) => n < 0)) {
    return "negative";
  }
  if (fields[0] === "") {
    return "emptyId";
  }
  const predecessors = predecessorsOf(fields.slice(needed));
  const base = { id: fields[0], name: fields[1], predecessors };
  if (mode === "cpm") {
    return { ...base, duration: values[0] };
  }
  const [a, m, b] = values;
  if (!(a <= m && m <= b)) {
    return "order";
  }
  return { ...base, duration: pertEstimate(a, m, b).te, estimate: { a, m, b } };
}

/**
 * Parses "ID, name, duration, predecessors…" lines (CPM) or "ID, name, a, m, b,
 * predecessors…" lines (PERT). A first line whose durations are not numbers is a header;
 * lines starting with # are comments. In PERT mode the activity's duration is te.
 */
export function parseActivities(
  raw: string,
  mode: ParseMode,
  parse: (text: string) => number | null
): ParsedActivities {
  const activities: ActivityInput[] = [];
  let first = true;
  for (const [index, line] of raw.split(LINE_SPLIT).entries()) {
    const text = line.trim();
    if (text === "" || text.startsWith("#")) {
      continue;
    }
    const outcome = activityFromFields(splitFields(text), mode, parse);
    const wasFirst = first;
    first = false;
    if (outcome === "header" && wasFirst) {
      continue;
    }
    if (typeof outcome === "string") {
      const reason = outcome === "header" ? "duration" : outcome;
      return { activities, problem: { line: index + 1, text, reason } };
    }
    activities.push(outcome);
  }
  return { activities, problem: null };
}
